import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { SubBadge } from "../ProjectUnitsTab";
import { useColumnResize } from "@/hooks/useColumnResize";
import { UserPlus } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PAGE_SIZE = 20;

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

interface GridData {
  scopeMap: Map<string, any>;
  woLineMap: Map<string, { vendor_id: string; vendor_name: string; isBilled: boolean; isComplete: boolean }>;
  sovTotals: Record<string, number>;
  sovMismatches: string[];
  vendorLegend: Map<string, { id: string; name: string }>;
}

interface Props {
  units: any[];
  sovLines: any[];
  gridData: GridData | null;
  onAssign: (sovLineId: string) => void;
}

type CellPos = { row: number; col: number };
const cellId = (row: number, col: number) => `${row}:${col}`;
const sameCellPos = (a: CellPos | null, b: CellPos | null) =>
  a && b ? a.row === b.row && a.col === b.col : a === b;

export const UnitsGridCard = ({ units, sovLines, gridData, onAssign }: Props) => {
  const { widths: colWidths, getResizeHandle } = useColumnResize({ minWidth: 60 });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [lastClickedIdx, setLastClickedIdx] = useState<number | null>(null);
  const sentinelRef = useRef<HTMLTableRowElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Cell selection state
  const [activeCell, setActiveCell] = useState<CellPos | null>(null);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [cellAnchor, setCellAnchor] = useState<CellPos | null>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  // Track which columns have been "select-all" toggled
  const [selectedColumns, setSelectedColumns] = useState<Set<number>>(new Set());

  // Reset pagination when units change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setSelectedRows(new Set());
    setActiveCell(null);
    setSelectedCells(new Set());
    setSelectedColumns(new Set());
    setPopupPos(null);
  }, [units?.length]);

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && visibleCount < units.length) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, units.length));
        }
      },
      { root: container, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, units.length]);

  const visibleUnits = useMemo(() => units.slice(0, visibleCount), [units, visibleCount]);
  const maxRow = visibleUnits.length - 1;
  const maxCol = sovLines.length - 1;

  // Build range selection set
  const buildRange = useCallback((from: CellPos, to: CellPos): Set<string> => {
    const set = new Set<string>();
    const r1 = Math.min(from.row, to.row);
    const r2 = Math.max(from.row, to.row);
    const c1 = Math.min(from.col, to.col);
    const c2 = Math.max(from.col, to.col);
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        set.add(cellId(r, c));
      }
    }
    return set;
  }, []);

  // Compute popup position relative to card
  const updatePopupPos = useCallback((row: number, col: number) => {
    const container = scrollRef.current;
    const card = cardRef.current;
    if (!container || !card) return;
    const cellEl = container.querySelector(`[data-cell="${cellId(row, col)}"]`);
    if (!cellEl) return;
    const cellRect = cellEl.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    setPopupPos({
      top: cellRect.bottom - cardRect.top + 4,
      left: Math.min(cellRect.left - cardRect.left, cardRect.width - 260),
    });
  }, []);

  // Toggle all cells in a column (clicking the product header)
  const handleColumnToggle = useCallback(
    (colIdx: number) => {
      setSelectedRows(new Set());
      setActiveCell(null);
      setCellAnchor(null);
      setPopupPos(null);

      const isSelected = selectedColumns.has(colIdx);

      if (isSelected) {
        // Deselect all cells in this column
        setSelectedCells((prev) => {
          const next = new Set(prev);
          for (let r = 0; r <= maxRow; r++) {
            next.delete(cellId(r, colIdx));
          }
          return next;
        });
        setSelectedColumns((prev) => {
          const next = new Set(prev);
          next.delete(colIdx);
          return next;
        });
      } else {
        // Select all cells in this column
        setSelectedCells((prev) => {
          const next = new Set(prev);
          for (let r = 0; r <= maxRow; r++) {
            next.add(cellId(r, colIdx));
          }
          return next;
        });
        setSelectedColumns((prev) => {
          const next = new Set(prev);
          next.add(colIdx);
          return next;
        });
      }
    },
    [maxRow, selectedColumns]
  );

  // Handle cell click — toggle-based so individual cells can be deselected
  const handleCellClick = useCallback(
    (row: number, col: number, e: React.MouseEvent) => {
      e.stopPropagation();
      const pos: CellPos = { row, col };

      if (e.shiftKey && activeCell) {
        const anchor = cellAnchor ?? activeCell;
        setSelectedCells(buildRange(anchor, pos));
        setActiveCell(pos);
      } else if (e.metaKey || e.ctrlKey) {
        setSelectedCells((prev) => {
          const next = new Set(prev);
          const id = cellId(row, col);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        setActiveCell(pos);
        setCellAnchor(pos);
        // If we deselected a cell, clear the column's "all selected" state
        setSelectedColumns((prev) => {
          const next = new Set(prev);
          next.delete(col);
          return next;
        });
      } else {
        // Plain click: if this cell is already selected, deselect it; otherwise select only it
        const id = cellId(row, col);
        setSelectedCells((prev) => {
          if (prev.has(id) && prev.size === 1) {
            return new Set();
          }
          if (prev.has(id)) {
            const next = new Set(prev);
            next.delete(id);
            return next;
          }
          return new Set([id]);
        });
        setActiveCell(pos);
        setCellAnchor(pos);
        setSelectedColumns(new Set());
      }
      setSelectedRows(new Set());
      requestAnimationFrame(() => updatePopupPos(row, col));
    },
    [activeCell, cellAnchor, buildRange, updatePopupPos]
  );

  // Keyboard navigation
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeCell) return;
      if (maxCol < 0 || maxRow < 0) return;

      let nextRow = activeCell.row;
      let nextCol = activeCell.col;
      let handled = false;

      switch (e.key) {
        case "ArrowUp":
          nextRow = Math.max(0, activeCell.row - 1);
          handled = true;
          break;
        case "ArrowDown":
          nextRow = Math.min(maxRow, activeCell.row + 1);
          handled = true;
          break;
        case "ArrowLeft":
          nextCol = Math.max(0, activeCell.col - 1);
          handled = true;
          break;
        case "ArrowRight":
          nextCol = Math.min(maxCol, activeCell.col + 1);
          handled = true;
          break;
        case "Escape":
          setActiveCell(null);
          setSelectedCells(new Set());
          setCellAnchor(null);
          setPopupPos(null);
          handled = true;
          break;
        default:
          return;
      }

      if (!handled) return;
      e.preventDefault();

      const next: CellPos = { row: nextRow, col: nextCol };

      // Ensure row is visible (auto-load more if needed)
      if (nextRow >= visibleCount - 1 && visibleCount < units.length) {
        setVisibleCount((c) => Math.min(c + PAGE_SIZE, units.length));
      }

      if (e.shiftKey) {
        const anchor = cellAnchor ?? activeCell;
        setSelectedCells(buildRange(anchor, next));
      } else {
        setSelectedCells(new Set([cellId(nextRow, nextCol)]));
        setCellAnchor(next);
      }
      setActiveCell(next);
      setSelectedRows(new Set());

      requestAnimationFrame(() => {
        const cellEl = container.querySelector(`[data-cell="${cellId(nextRow, nextCol)}"]`);
        cellEl?.scrollIntoView({ block: "nearest", inline: "nearest" });
        updatePopupPos(nextRow, nextCol);
      });
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => container.removeEventListener("keydown", handleKeyDown);
  }, [activeCell, cellAnchor, maxRow, maxCol, visibleCount, units.length, buildRange]);

  // Row click handler with Shift/Ctrl support (for checkbox column / unit column)
  const handleRowClick = useCallback(
    (unitId: string, idx: number, e: React.MouseEvent) => {
      setActiveCell(null);
      setSelectedCells(new Set());
      setCellAnchor(null);
      setPopupPos(null);

      setSelectedRows((prev) => {
        const next = new Set(prev);

        if (e.shiftKey && lastClickedIdx !== null) {
          const start = Math.min(lastClickedIdx, idx);
          const end = Math.max(lastClickedIdx, idx);
          for (let i = start; i <= end; i++) {
            next.add(units[i].id);
          }
        } else if (e.metaKey || e.ctrlKey) {
          if (next.has(unitId)) next.delete(unitId);
          else next.add(unitId);
        } else {
          if (next.size === 1 && next.has(unitId)) {
            next.clear();
          } else {
            next.clear();
            next.add(unitId);
          }
        }
        return next;
      });
      setLastClickedIdx(idx);
    },
    [lastClickedIdx, units]
  );

  const allVisibleSelected = visibleUnits.length > 0 && visibleUnits.every((u: any) => selectedRows.has(u.id));

  const toggleSelectAll = () => {
    setSelectedRows((prev) => {
      if (allVisibleSelected) return new Set();
      const next = new Set(prev);
      visibleUnits.forEach((u: any) => next.add(u.id));
      return next;
    });
  };

  // Summary calculations — support both row and cell selection modes
  const summary = useMemo(() => {
    const hasCells = selectedCells.size > 0;
    const hasRows = selectedRows.size > 0;
    if (!hasCells && !hasRows) return null;
    if (!gridData) return null;

    let grandSum = 0;
    let grandCount = 0;
    let grandMin = Infinity;
    let grandMax = -Infinity;
    const perColumn: Record<string, { sum: number; count: number; min: number; max: number; label: string }> = {};
    sovLines.forEach((sl: any) => {
      perColumn[sl.id] = { sum: 0, count: 0, min: Infinity, max: -Infinity, label: sl.description };
    });

    const processValue = (slId: string, qty: number) => {
      if (qty > 0) {
        perColumn[slId].sum += qty;
        perColumn[slId].count += 1;
        perColumn[slId].min = Math.min(perColumn[slId].min, qty);
        perColumn[slId].max = Math.max(perColumn[slId].max, qty);
        grandSum += qty;
        grandCount += 1;
        grandMin = Math.min(grandMin, qty);
        grandMax = Math.max(grandMax, qty);
      }
    };

    if (hasCells) {
      selectedCells.forEach((id) => {
        const [rStr, cStr] = id.split(":");
        const r = Number(rStr);
        const c = Number(cStr);
        if (r < 0 || r >= units.length || c < 0 || c >= sovLines.length) return;
        const unit = units[r];
        const sl = sovLines[c];
        const si = gridData.scopeMap.get(`${unit.id}__${sl.id}`);
        const qty = si?.has_scope ? Number(si.quantity) : 0;
        processValue(sl.id, qty);
      });

      const activeColumns = Object.entries(perColumn).filter(([, v]) => v.count > 0);
      return {
        count: selectedCells.size,
        label: "cells",
        grandSum, grandCount,
        grandAvg: grandCount > 0 ? grandSum / grandCount : 0,
        grandMin: grandCount > 0 ? grandMin : 0,
        grandMax: grandCount > 0 ? grandMax : 0,
        activeColumns,
      };
    }

    selectedRows.forEach((unitId) => {
      sovLines.forEach((sl: any) => {
        const si = gridData.scopeMap.get(`${unitId}__${sl.id}`);
        const qty = si?.has_scope ? Number(si.quantity) : 0;
        processValue(sl.id, qty);
      });
    });

    const activeColumns = Object.entries(perColumn).filter(([, v]) => v.count > 0);
    return {
      count: selectedRows.size,
      label: "rows",
      grandSum, grandCount,
      grandAvg: grandCount > 0 ? grandSum / grandCount : 0,
      grandMin: grandCount > 0 ? grandMin : 0,
      grandMax: grandCount > 0 ? grandMax : 0,
      activeColumns,
    };
  }, [selectedCells, selectedRows, gridData, sovLines, units]);

  const showPopup = summary && selectedCells.size >= 2 && popupPos;

  return (
    <Card ref={cardRef} className="relative">
      {/* Showing X of Y */}
      {units.length > PAGE_SIZE && (
        <div className="px-3 pt-2 text-[11px] text-muted-foreground">
          Showing {Math.min(visibleCount, units.length)} of {units.length} units
        </div>
      )}

      <div
        ref={scrollRef}
        className="max-h-[600px] overflow-y-auto overflow-x-auto focus:outline-none"
        tabIndex={0}
      >
        <table className="w-full text-xs border-collapse" style={{ tableLayout: "fixed" }}>
          <thead className="sticky top-0 z-20">
            <tr className="border-b bg-muted/95 backdrop-blur-sm">
              <th className="sticky left-0 bg-muted/95 px-1 py-2 text-center font-medium z-30 w-8">
                <Checkbox
                  checked={allVisibleSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                  className="h-3.5 w-3.5"
                />
              </th>
              <th className="sticky left-8 bg-muted/95 px-3 py-2 text-left font-medium z-30">Unit</th>
              <th className="px-2 py-2 text-left font-medium">Floor</th>
              {sovLines.map((sl: any, colIdx: number) => {
                const isColSelected = selectedColumns.has(colIdx);
                return (
                  <th
                    key={sl.id}
                    className={cn(
                      "relative px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted/80 group",
                      isColSelected && "bg-blue-100/60 dark:bg-blue-900/30"
                    )}
                    style={{ width: colWidths[sl.id] || 140, minWidth: 80 }}
                    onClick={() => handleColumnToggle(colIdx)}
                  >
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="text-xs leading-tight break-words pr-4">{sl.description}</div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="text-sm font-medium">{sl.description}</p>
                          <p className="text-xs text-muted-foreground">{sl.unit} — Click header to select column</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div className="flex items-center justify-end gap-1 mt-0.5">
                      <span className="text-[10px] text-muted-foreground font-normal">{sl.unit}</span>
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-primary/20"
                        title={`Assign sub to: ${sl.description}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAssign(sl.id);
                        }}
                      >
                        <UserPlus className="h-3 w-3 text-muted-foreground" />
                      </button>
                    </div>
                    {getResizeHandle(sl.id)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleUnits.map((unit: any, idx: number) => {
              const isRowSelected = selectedRows.has(unit.id);
              return (
                <tr
                  key={unit.id}
                  className="border-b transition-colors hover:bg-muted/30"
                >
                  <td
                    className={cn(
                      "sticky left-0 px-1 py-1.5 text-center z-10 cursor-pointer",
                      isRowSelected ? "bg-primary/10" : "bg-background"
                    )}
                    onClick={(e) => handleRowClick(unit.id, idx, e)}
                  >
                    <Checkbox
                      checked={isRowSelected}
                      onCheckedChange={() => {}}
                      className="h-3.5 w-3.5 pointer-events-none"
                      tabIndex={-1}
                    />
                  </td>
                  <td
                    className={cn(
                      "sticky left-8 px-3 py-1.5 font-mono font-medium z-10 cursor-pointer",
                      isRowSelected ? "bg-primary/10" : "bg-background"
                    )}
                    onClick={(e) => handleRowClick(unit.id, idx, e)}
                  >
                    {unit.unit_number}
                  </td>
                  <td className={cn(
                    "px-2 py-1.5 text-muted-foreground",
                    isRowSelected ? "bg-primary/10" : "bg-background"
                  )}>{unit.floor_number || "—"}</td>
                  {sovLines.map((sl: any, colIdx: number) => {
                    const si = gridData?.scopeMap.get(`${unit.id}__${sl.id}`);
                    const qty = si?.has_scope ? Number(si.quantity) : 0;
                    const mapKey = `${unit.id}__${sl.id}`;
                    const wolInfo = gridData?.woLineMap.get(mapKey);
                    const isClosed = wolInfo?.isBilled || wolInfo?.isComplete;
                    const vendorId = wolInfo?.vendor_id;
                    const vendorName = wolInfo?.vendor_name;
                    const isAssigned = !!wolInfo;

                    const cId = cellId(idx, colIdx);
                    const isCellSelected = selectedCells.has(cId);
                    const isActive = activeCell?.row === idx && activeCell?.col === colIdx;

                    let cellClass = "bg-background text-muted-foreground/30";
                    if (qty > 0 && isAssigned && isClosed) {
                      cellClass = "bg-muted/20 text-muted-foreground/40";
                    } else if (qty > 0 && isAssigned) {
                      cellClass = "bg-primary/5 text-primary";
                    } else if (qty > 0) {
                      cellClass = "bg-warning/5 text-warning";
                    }

                    const cellBg = isCellSelected
                      ? "!bg-blue-200/50 dark:!bg-blue-800/40 !text-foreground"
                      : isRowSelected
                        ? "!bg-primary/10"
                        : cellClass;

                    return (
                      <td
                        key={sl.id}
                        data-cell={cId}
                        className={cn(
                          "px-2 py-1.5 text-right font-mono cursor-cell transition-colors",
                          cellBg,
                          isActive && "ring-2 ring-inset ring-primary"
                        )}
                        onClick={(e) => handleCellClick(idx, colIdx, e)}
                      >
                        {qty > 0 ? (
                          <div className="flex items-center justify-end gap-1">
                            <span className={isClosed ? "opacity-40" : ""}>{fmt(qty)}</span>
                            {vendorId && vendorName && (
                              <SubBadge vendorId={vendorId} vendorName={vendorName} greyed={!!isClosed} />
                            )}
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {/* Infinite scroll sentinel */}
            {visibleCount < units.length && (
              <tr ref={sentinelRef}>
                <td colSpan={sovLines.length + 3} className="py-2 text-center text-[10px] text-muted-foreground">
                  Loading more…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Floating summary popup near active cell */}
      {showPopup && summary && (
        <div
          className="absolute z-30 rounded-lg border bg-popover text-popover-foreground shadow-lg px-3 py-2 text-xs font-mono animate-in fade-in-0 zoom-in-95 duration-150"
          style={{
            top: popupPos.top,
            left: Math.max(8, popupPos.left),
            maxWidth: 260,
          }}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-semibold">✓ {summary.count} {summary.label}</span>
            <span className="text-muted-foreground">Sum: {fmt(summary.grandSum)}</span>
            <span className="text-muted-foreground">Avg: {fmt(summary.grandAvg)}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-muted-foreground">
            <span>Min: {fmt(summary.grandMin)}</span>
            <span>Max: {fmt(summary.grandMax)}</span>
            <span>Count: {summary.grandCount}</span>
          </div>
        </div>
      )}

      {/* Sticky SOV footer — always visible below scroll area */}
      <div className="border-t-2 overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <tbody>
            <tr className="bg-muted/30 font-semibold">
              <td className="w-8"></td>
              <td className="px-3 py-1.5">SOV TOTAL</td>
              <td className="px-2"></td>
              {sovLines.map((sl: any) => (
                <td key={sl.id} className="px-2 py-1.5 text-right font-mono min-w-[80px]">
                  {fmt(gridData?.sovTotals[sl.id] ?? 0)}
                </td>
              ))}
            </tr>
            <tr className="bg-muted/30 font-semibold">
              <td className="w-8"></td>
              <td className="px-3 py-1.5">SOV LINE</td>
              <td className="px-2"></td>
              {sovLines.map((sl: any) => (
                <td key={sl.id} className="px-2 py-1.5 text-right font-mono min-w-[80px]">
                  {fmt(Number(sl.quantity))}
                </td>
              ))}
            </tr>
            <tr className="bg-muted/30">
              <td className="w-8"></td>
              <td className="px-3 py-1.5 font-semibold">DIFF</td>
              <td className="px-2"></td>
              {sovLines.map((sl: any) => {
                const total = gridData?.sovTotals[sl.id] ?? 0;
                const diff = total - Number(sl.quantity);
                const matches = Math.abs(diff) < 0.01;
                return (
                  <td key={sl.id} className={cn("px-2 py-1.5 text-right font-mono text-xs min-w-[80px]", matches ? "text-primary" : "text-destructive")}>
                    {matches ? "0 ✅" : `${diff > 0 ? "+" : ""}${fmt(diff)} ⚠️`}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Excel-like summary bar */}
      {summary && (
        <div className="border-t bg-muted/95 backdrop-blur-sm px-3 py-2 space-y-1">
          <div className="flex items-center gap-4 text-xs font-mono flex-wrap">
            <span className="font-semibold text-foreground">
              ✓ {summary.count} {summary.label}
            </span>
            <span className="text-muted-foreground">Sum: {fmt(summary.grandSum)}</span>
            <span className="text-muted-foreground">Avg: {fmt(summary.grandAvg)}</span>
            <span className="text-muted-foreground">Min: {fmt(summary.grandMin)}</span>
            <span className="text-muted-foreground">Max: {fmt(summary.grandMax)}</span>
            <span className="text-muted-foreground">Count: {summary.grandCount}</span>
          </div>
          {summary.activeColumns.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-0.5">
              {summary.activeColumns.map(([id, col]) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1.5 shrink-0 rounded-md bg-background/80 border px-2 py-0.5 text-[10px] font-mono text-muted-foreground"
                >
                  <span className="font-medium text-foreground truncate max-w-[80px]">{col.label}</span>
                  Sum: {fmt(col.sum)} · Avg: {fmt(col.count > 0 ? col.sum / col.count : 0)} · Min: {fmt(col.min === Infinity ? 0 : col.min)} · Max: {fmt(col.max === -Infinity ? 0 : col.max)} · Count: {col.count}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
